// Test Audio Files — desktop tab (dev-only, Ethosphere org only).
//
// Route: /test-audio. Lists the org's reusable bulk-import sources and
// surfaces two actions:
//
//   - Upload   → opens UploadTestAudioDialog
//   - Run row  → opens RunTestAudioDialog (creates a new thread from the
//                normalised WAV via POST /threads/bulk-import).
//
// Visibility is enforced at TWO layers — the tab is only rendered when
// `getTestAudioFeatureStatus()` returns enabled=true, AND every backend
// endpoint 404s for unauthorised callers. The page itself is still defensive
// and shows an empty state if the API surfaces an error.

import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
} from "@chakra-ui/react";
import { AddIcon } from "@chakra-ui/icons";

import PageLayout from "./PageLayout";
import {
  getTestAudioFeatureStatus,
  listTestAudioFiles,
} from "../services/testAudioService";
import UploadTestAudioDialog from "./UploadTestAudioDialog";
import RunTestAudioDialog from "./RunTestAudioDialog";
import { formatDate } from "../utils/dateUtils";

function formatDuration(ms) {
  if (!ms || ms <= 0) return "—";
  const totalSec = Math.round(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function StatusBadge({ status, failureReason }) {
  if (status === "READY") {
    return (
      <Badge colorScheme="green" data-testid="status-badge-ready">
        Ready
      </Badge>
    );
  }
  if (status === "FAILED") {
    return (
      <Tooltip
        hasArrow
        label={failureReason || "Transcode failed"}
        placement="top"
      >
        <Badge colorScheme="red" data-testid="status-badge-failed">
          Failed
        </Badge>
      </Tooltip>
    );
  }
  // TRANSCODING (default).
  return (
    <HStack spacing={1} data-testid="status-badge-transcoding">
      <Spinner size="xs" />
      <Text fontSize="xs" color="gray.600">
        Transcoding
      </Text>
    </HStack>
  );
}

const TestAudioFilesPage = ({
  // Injected by tests to bypass the network.
  listFn,
  featureStatusFn,
}) => {
  const doList = listFn || listTestAudioFiles;
  const doFeatureStatus = featureStatusFn || getTestAudioFeatureStatus;

  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [featureEnabled, setFeatureEnabled] = useState(null); // null = unknown
  const [uploadOpen, setUploadOpen] = useState(false);
  const [runOpen, setRunOpen] = useState(false);
  const [runTarget, setRunTarget] = useState(null);

  const fetchFiles = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // `status=""` would normally drop the filter; pass null sentinel so
      // we get back all rows (including in-flight TRANSCODING ones) and the
      // user sees their pending uploads while waiting on the worker.
      const response = await doList({ status: null, limit: 100 });
      setFiles(response?.items || []);
    } catch (e) {
      const message =
        e?.response?.data?.detail || e?.message || "Failed to load test audio files.";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [doList]);

  // Gate the entire page on the same flag the nav uses. The nav already
  // hides the tab, but a deep-linked URL could land here directly.
  useEffect(() => {
    (async () => {
      try {
        const { enabled } = await doFeatureStatus();
        setFeatureEnabled(!!enabled);
      } catch {
        setFeatureEnabled(false);
      }
    })();
  }, [doFeatureStatus]);

  useEffect(() => {
    if (featureEnabled) fetchFiles();
  }, [featureEnabled, fetchFiles]);

  if (featureEnabled === false) {
    return (
      <PageLayout>
        <VStack spacing={5} align="stretch" p={6} data-testid="test-audio-disabled">
          <Alert status="warning" borderRadius="md">
            <AlertIcon />
            <Box flex={1}>
              <AlertTitle fontSize="sm">Not available</AlertTitle>
              <AlertDescription fontSize="xs">
                Test Audio Files is a dev-only feature scoped to the Ethosphere organization.
              </AlertDescription>
            </Box>
          </Alert>
        </VStack>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <VStack spacing={5} align="stretch" p={6} data-testid="test-audio-files-page">
        <Flex justify="space-between" align="center">
          <Text fontSize="xl" fontWeight="bold">
            Test Audio Files
          </Text>
          <HStack spacing={2}>
            <Button
              colorScheme="blue"
              size="sm"
              px={5}
              borderRadius="md"
              fontWeight="semibold"
              leftIcon={<AddIcon />}
              onClick={() => setUploadOpen(true)}
              data-testid="test-audio-upload-button"
            >
              Upload
            </Button>
          </HStack>
        </Flex>

        <Text fontSize="sm" color="gray.600">
          Register an audio file once; spin up a new thread from it as many times as you want.
        </Text>

        {loading && (
          <Flex justify="center" py={16}>
            <Spinner size="lg" />
          </Flex>
        )}

        {!loading && error && (
          <Alert status="error" borderRadius="md" data-testid="test-audio-error">
            <AlertIcon />
            <Box flex={1}>
              <AlertTitle fontSize="sm">Could not load test audio files</AlertTitle>
              <AlertDescription fontSize="xs">{error}</AlertDescription>
            </Box>
            <Button size="xs" onClick={fetchFiles}>
              Retry
            </Button>
          </Alert>
        )}

        {!loading && !error && files.length === 0 && (
          <Box
            py={16}
            textAlign="center"
            bg="gray.50"
            borderRadius="lg"
            borderWidth="1px"
            borderColor="gray.200"
            borderStyle="dashed"
            data-testid="test-audio-empty"
          >
            <Text color="gray.500" fontSize="sm">
              No test audio yet. Click <strong>Upload</strong> to register your first file.
            </Text>
          </Box>
        )}

        {!loading && !error && files.length > 0 && (
          <Box
            overflowX="auto"
            borderRadius="lg"
            borderWidth="1px"
            borderColor="gray.200"
            bg="white"
          >
            <Table variant="unstyled" size="sm">
              <Thead>
                <Tr
                  bg="gray.50"
                  borderBottomWidth="1px"
                  borderBottomColor="gray.200"
                >
                  <Th py={3} px={4} fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider">
                    Name
                  </Th>
                  <Th py={3} px={4} fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider">
                    Status
                  </Th>
                  <Th py={3} px={4} fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider">
                    Duration
                  </Th>
                  <Th py={3} px={4} fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider">
                    Created
                  </Th>
                  <Th py={3} px={4} fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider">
                    Last Used
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {files.map((f) => (
                  <Tr
                    key={f.id}
                    data-testid={`test-audio-row-${f.id}`}
                    cursor={f.status === "READY" ? "pointer" : "default"}
                    onClick={() => {
                      if (f.status !== "READY") return;
                      setRunTarget(f);
                      setRunOpen(true);
                    }}
                    borderBottomWidth="1px"
                    borderBottomColor="gray.100"
                    _hover={f.status === "READY" ? { bg: "gray.50" } : undefined}
                    transition="background 0.1s"
                  >
                    <Td py={3} px={4}>
                      <VStack align="start" spacing={0}>
                        <Text fontSize="sm" fontWeight="medium">
                          {f.name}
                        </Text>
                        {f.description && (
                          <Text fontSize="xs" color="gray.500" noOfLines={1}>
                            {f.description}
                          </Text>
                        )}
                      </VStack>
                    </Td>
                    <Td py={3} px={4}>
                      <StatusBadge
                        status={f.status}
                        failureReason={f.failure_reason}
                      />
                    </Td>
                    <Td py={3} px={4}>
                      <Text fontSize="sm" color="gray.600">
                        {formatDuration(f.duration_ms)}
                      </Text>
                    </Td>
                    <Td py={3} px={4}>
                      <Text fontSize="sm" color="gray.600">
                        {formatDate(f.created_at)}
                      </Text>
                    </Td>
                    <Td py={3} px={4}>
                      <Text fontSize="sm" color="gray.600">
                        {f.last_used_at ? formatDate(f.last_used_at) : "—"}
                      </Text>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        )}

        <UploadTestAudioDialog
          isOpen={uploadOpen}
          onClose={() => setUploadOpen(false)}
          onUploaded={() => {
            setUploadOpen(false);
            fetchFiles();
          }}
        />

        <RunTestAudioDialog
          isOpen={runOpen}
          file={runTarget}
          onClose={() => {
            setRunOpen(false);
            setRunTarget(null);
          }}
        />
      </VStack>
    </PageLayout>
  );
};

export { StatusBadge, formatDuration };
export default TestAudioFilesPage;
